import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectCards } from "swiper/modules";
import { HelpCircle, X, AlertTriangle, Lock } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SetPinDialog } from "@/components/SetPinDialog";
import { oxpayAPI } from "@/lib/oxpay";
import { toast } from "react-toastify";

// Import Swiper styles
import "swiper/css";
import "swiper/css/effect-cards";

// Mock data for demo - will be replaced with actual card data
const mockCards = [
  // {
  //   id: 1,
  //   number: "****-****-****-1572",
  //   balance: 785.0,
  //   name: "Main Card",
  //   isActive: true,
  //   isLocked: false,
  //   isStolen: false,
  // },
  // {
  //   id: 2,
  //   number: "****-****-****-9999",
  //   balance: 1200.0,
  //   name: "Business Card",
  //   isActive: false,
  //   isLocked: false,
  //   isStolen: false,
  // },
  // {
  //   id: 3,
  //   number: "****-****-****-7777",
  //   balance: 500.0,
  //   name: "Savings Card",
  //   isActive: false,
  //   isLocked: false,
  //   isStolen: false,
  // },
];

export default function CardSwiper({
  userEmail = "",
  showCardSelect = false,
  cardsData = [],
  showLockStolen = false,
  showActivation = false,
  onCardChange = null,
}) {
  const navigate = useNavigate();
  const [swiper, setSwiper] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [showSetPinDialog, setShowSetPinDialog] = useState(false);

  // cards 테이블 데이터를 카드 표시용 형식으로 변환
  const cards =
    cardsData.length > 0
      ? cardsData.map((card) => ({
          id: card.id,
          number: card.card_number
            ? `****-****-****-${card.card_number.slice(-4)}`
            : `Order #${card.fellas_order_id}`,
          balance: card.card_balance || 0,
          name: `${card.card_type?.replace("_", " ") || "FELLAS"} Card`,
          isActive: card.is_active || false,
          isLocked:
            card.card_status === "blocked" || card.card_status === "frozen",
          isStolen: card.is_stolen || false,
          isSetPIN: card.is_set_pin || false,
          status: card.card_status,
          orderStatus: card.payment_status,
        }))
      : mockCards;

  const [selectedCard, setSelectedCard] = useState(cards[0]);

  // 초기 카드 선택 시 콜백 호출
  useEffect(() => {
    if (onCardChange && cards.length > 0) {
      onCardChange(cards[0]);
    }
  }, []);

  const handleTopup = () => {
    window.open("https://mining.mimbonode.io", "_blank");
    setShowTopupModal(false);
  };

  const handleSetPin = async (pin) => {
    try {
      const cardData = cardsData[activeIndex];
      if (!cardData?.oxpay_card_id) {
        throw new Error("Card ID not found");
      }

      const { data: account } = await supabase
        .from("accounts")
        .select("oxpay_user_id, phone_number, oxpay_user_data")
        .eq("email", userEmail)
        .single();

      if (
        !account?.oxpay_user_id ||
        !account?.phone_number ||
        !account?.oxpay_user_data ||
        !account?.oxpay_user_data.mobile
      ) {
        throw new Error("User information not found");
      }

      // Call OxPay API to set PIN
      const response = await oxpayAPI.setPin(
        cardData.oxpay_card_id,
        account.oxpay_user_id,
        pin,
        account.oxpay_user_data.mobile.number,
        account.oxpay_user_data.mobile.country_code
      );

      if (response?.headers?.status_code === "error") {
        throw new Error(response?.body?.description || "Failed to set PIN");
      }

      const { error: dbError } = await supabase
        .from("active_cards")
        .update({
          is_set_pin: true,
        })
        .eq("oxpay_card_id", cardData.oxpay_card_id);

      if (dbError) {
        console.error("Failed to update card information:", dbError);
        throw new Error(
          "Successfully set PIN, but failed to update card information"
        );
      }

      toast.success("PIN has been set successfully");
    } catch (error) {
      console.error("Failed to set PIN:", error);
      throw error;
    }
  };

  const handleCardSelect = (cardId) => {
    const index = cards.findIndex((card) => card.id.toString() === cardId);
    if (index !== -1 && swiper) {
      swiper.slideTo(index);
      setActiveIndex(index);
      setSelectedCard(cards[index]);
    }
  };

  useEffect(() => {
    if (swiper) {
      swiper.on("slideChange", () => {
        const newIndex = swiper.activeIndex;
        setActiveIndex(newIndex);
        setSelectedCard(cards[newIndex]);
        if (onCardChange) {
          onCardChange(cards[newIndex]);
        }
      });
    }
  }, [swiper, onCardChange]);

  return (
    <>
      <Card className="w-full bg-card/80 p-4 select-none">
        {showCardSelect && (
          <div className="flex flex-col items-end mb-3">
            <Button size="sm" className="text-xs h-6 px-2 py-0 mb-2">
              Card{" "}
              {cards.length > 0 ? `${activeIndex + 1}/${cards.length}` : "0/0"}
            </Button>

            {cards.length > 0 ? (
              <Select
                value={selectedCard?.id?.toString()}
                onValueChange={handleCardSelect}
              >
                <SelectTrigger className="w-full !h-12 text-lg my-2">
                  <SelectValue placeholder="Select a card" />
                </SelectTrigger>
                <SelectContent>
                  {cards.map((card) => (
                    <SelectItem key={card.id} value={card.id?.toString()}>
                      <div className="flex items-center justify-between w-full gap-2">
                        <span className="text-sm font-medium">
                          {card.number}
                        </span>
                        <span
                          className={`text-xs font-semibold ${
                            card.isActive
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400"
                          }`}
                        >
                          {card.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Select disabled>
                <SelectTrigger className="w-full !h-12 text-lg my-2">
                  <SelectValue placeholder="No cards available" />
                </SelectTrigger>
              </Select>
            )}
          </div>
        )}
        <div className="flex items-center justify-center gap-[18px] px-1 overflow-hidden">
          {/* Left Arrow */}
          {cards.length > 0 ? (
            <button
              onClick={() => swiper?.slidePrev()}
              className="text-[#81A4B7] hover:opacity-70 transition-opacity cursor-pointer flex-shrink-0"
            >
              <span className="text-xl font-semibold">〈</span>
            </button>
          ) : (
            <div className="w-6 flex-shrink-0" />
          )}

          {/* Card Container */}
          <div className="flex-1 min-w-0">
            {cards.length === 0 ? (
              /* No Cards Message */
              <div className="w-full max-w-[267px] mx-auto">
                <div className="rounded-xl overflow-hidden shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  {/* Card Top Section with gradient */}
                  <div
                    className="relative h-[150px] p-4 pb-3"
                    style={{
                      background:
                        "linear-gradient(139deg, #9CA3AF 63%, #6B7280 79%)",
                    }}
                  >
                    {/* Overlay for content area */}
                    <div className="absolute inset-0 m-0 rounded-t-xl bg-[#B8BFC7]" />

                    {/* Content */}
                    <div className="relative z-10 h-full flex flex-col justify-center items-center">
                      <div className="text-center">
                        <h3 className="text-white text-lg font-semibold mb-2">
                          No Cards Registered
                        </h3>
                        <p className="text-white/90 text-sm">
                          Please purchase and register a card from the card
                          purchase site
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card Bottom Section */}
                  <div className="px-6 py-6 bg-[#8B9298]"></div>
                </div>
              </div>
            ) : (
              <Swiper
                effect={"cards"}
                grabCursor={true}
                modules={[EffectCards]}
                onSwiper={setSwiper}
                onSlideChange={(swiper) => {
                  setActiveIndex(swiper.activeIndex);
                  setSelectedCard(cards[swiper.activeIndex]);
                }}
                className="w-full max-w-[267px] mx-auto"
                allowTouchMove={true}
                loop={false}
                initialSlide={0}
                slidesPerView={1}
                speed={300}
              >
                {cards.map((card) => (
                  <SwiperSlide key={card.id}>
                    <div className="rounded-xl overflow-hidden shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                      {/* Card Top Section with gradient */}
                      <div
                        className="relative h-[150px] p-4 pb-3"
                        style={{
                          background: card.isActive
                            ? "linear-gradient(139deg, #264C92 63%, #273F8F 79%)"
                            : "linear-gradient(139deg, #6B7280 63%, #4B5563 79%)",
                        }}
                      >
                        {/* Overlay for content area */}
                        <div
                          className={`absolute inset-0 m-0 rounded-t-xl ${
                            card.isActive ? "bg-[#597FC5]" : "bg-[#9CA3AF]"
                          }`}
                        />

                        {/* Content */}
                        <div className="relative z-10 h-full flex flex-col justify-between">
                          {/* Card Info at Top */}
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="text-white text-base font-semibold">
                                  {card.name || "Fellas Card"}
                                </h3>
                                {card.isLocked && (
                                  <span className="px-2 py-0.5 bg-red-500/80 text-white text-xs font-medium rounded">
                                    Locked
                                  </span>
                                )}
                              </div>
                              <p className="text-white text-sm font-semibold mt-2">
                                {card.number}
                              </p>
                            </div>
                            <HelpCircle className="w-[15px] h-[15px] text-white" />
                          </div>

                          {/* View Usage at Bottom */}
                          <div className="mt-auto">
                            <button
                              className="text-white text-xs font-medium cursor-pointer"
                              onClick={() => navigate("/history")}
                            >
                              View Usage &gt;
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Card Bottom Section */}
                      <div
                        className={`px-6 py-3 ${
                          card.isActive ? "bg-[#3B6FAA]" : "bg-[#6B7280]"
                        }`}
                      >
                        <div className="text-right">
                          <p className="text-white text-sm font-normal">
                            Balance
                          </p>
                          {card.balance !== "-" ? (
                            <p className="text-white text-2xl font-semibold mt-1">
                              $ {parseFloat(card.balance).toFixed(2)}
                            </p>
                          ) : (
                            <p className="text-white text-2xl font-semibold mt-1">
                              -
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            )}
          </div>

          {/* Right Arrow */}
          {cards.length > 0 ? (
            <button
              onClick={() => swiper?.slideNext()}
              className="text-[#81A4B7] hover:opacity-70 transition-opacity cursor-pointer flex-shrink-0"
            >
              <span className="text-xl font-semibold">〉</span>
            </button>
          ) : (
            <div className="w-6 flex-shrink-0" />
          )}
        </div>

        {/* Card Indicators */}
        {cards.length > 0 && (
          <div className="flex justify-center space-x-2 mt-4">
            {cards.map((_, index) => (
              <button
                key={index}
                onClick={() => swiper?.slideTo(index)}
                className={`h-2 rounded-full transition-all ${
                  index === activeIndex ? "w-4 bg-primary" : "w-2 bg-muted"
                }`}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Lock Stolen Button */}
      {showLockStolen && (
        <div className="flex justify-center mt-4">
          <div className="mt-4 flex justify-center gap-3">
            <Button
              variant="secondary"
              className="bg-primary hover:bg-primary/90 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:text-gray-200 px-2 sm:px-4"
              onClick={() => setShowSetPinDialog(true)}
              disabled={
                !selectedCard || !selectedCard.isActive || cards.length === 0
              }
            >
              Set PIN
            </Button>
            <Button
              variant="secondary"
              className="bg-primary hover:bg-primary/90 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:text-gray-200 px-2 sm:px-4"
              onClick={() =>
                navigate(selectedCard?.isLocked ? "/card-unlock" : "/card-lock")
              }
              disabled={
                !selectedCard || !selectedCard.isActive || cards.length === 0
              }
            >
              {selectedCard?.isLocked ? "Unlock Card" : "Lock Card"}
            </Button>
            <Button
              variant="secondary"
              className="bg-primary hover:bg-primary/90 disabled:bg-gray-400 disabled:hover:bg-gray-400 disabled:text-gray-200 px-2 sm:px-4"
              onClick={() => navigate("/card-stolen")}
              disabled={
                !selectedCard || !selectedCard.isActive || cards.length === 0
              }
            >
              Report Stolen
            </Button>
          </div>
        </div>
      )}

      {/* Set PIN Dialog */}
      <SetPinDialog
        open={showSetPinDialog}
        onOpenChange={setShowSetPinDialog}
        onSetPin={handleSetPin}
        cardNumber={cardsData[activeIndex]?.card_number}
      />

      {/* Card Activation Section */}
      {false && showActivation && selectedCard && !selectedCard.isActive && (
        <div className="mt-4 p-4 space-y-4">
          {/* Title */}
          <h3
            className={`text-lg font-semibold text-center ${
              selectedCard.isActive ? "text-muted-foreground" : ""
            }`}
          >
            Activate your Physical Card
          </h3>

          <div className="">
            <label
              className={`text-sm mb-2 block ${
                selectedCard.isActive ? "text-muted-foreground" : ""
              }`}
            >
              Activate and load your physical card to start using it right away.
              You can now conveniently use it at all online and offline
              merchants worldwide, and withdraw cash from ATMs.
            </label>
          </div>

          {/* Activate Button */}
          <Button
            className="w-full h-12"
            disabled={selectedCard.isActive}
            onClick={() => navigate("/card-activation")}
          >
            Activate
          </Button>
        </div>
      )}

      {/* Topup Button */}
      {false && (
        <div className="flex flex-col items-center justify-center mt-6 gap-2">
          <div className="flex items-center gap-2">Here is your top-up.</div>
          <Button
            onClick={() => setShowTopupModal(true)}
            className="h-6 px-2 text-[10px] font-semibold"
          >
            Topup Now
          </Button>
        </div>
      )}

      {/* Topup Modal */}
      {showTopupModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4">
          <div className="bg-background rounded-[20px] shadow-lg p-4 w-full max-w-[320px] flex flex-col gap-8">
            {/* Close button */}
            <div className="flex justify-end">
              <button onClick={() => setShowTopupModal(false)} className="p-0">
                <X className="w-6 h-6 text-[#7A7A7A]" />
              </button>
            </div>

            {/* Modal content */}
            <div className="flex flex-col gap-6 px-2">
              <div className="flex flex-col gap-2">
                <div className="flex justify-center mb-2">
                  <AlertTriangle className="w-12 h-12 text-yellow-500" />
                </div>
                <h3 className="text-base font-semibold text-center text-yellow-500 leading-relaxed">
                  Warning!!!
                </h3>
                <p className="text-sm text-center text-muted-foreground">
                  Please log in with the account matching your email address:
                </p>
                <p className="text-base font-bold text-center text-primary mt-2">
                  {userEmail}
                </p>
              </div>

              <Button
                onClick={handleTopup}
                className="w-full h-12 bg-primary hover:bg-primary/90 text-white font-semibold"
              >
                Go to Topup
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
